import { Injectable, inject } from '@angular/core';
import { NgKLoggerService } from '@kin/ngk-logger';

import { Endorsement, Policy } from 'app/home/common/services/legacy/policies/policies.model';
import {
  LegacyApiApplicant,
  LegacyApiAutomaticallyAppliedFunnelDiscount,
  LegacyApiCoverage,
  LegacyApiFee,
  LegacyApiLimitedFungiMetadata,
  LegacyApiPolicyMetadata,
  LegacyApiPolicyResponse,
} from 'app/home/common/services/legacy/policies/policies.service';
import { isDateYYYYMMDD } from 'app/home/common/utils/date-helper/date-helper';
import { OtherPolicyFeaturesMapperService } from 'app/home/create-quote/quote-review/additional-coverages/map-other-policy-features';
import { FloodProtectionMapperService } from 'app/home/create-quote/quote-review/flood-protection/map-flood-protection';
import { PrimaryCoverageMapperService } from 'app/home/create-quote/quote-review/primary-coverages/map-primary-coverages';
import { RspsMapperService } from 'app/home/create-quote/quote-review/rsps/map-rsps';
import { parseCoverageValue } from 'app/home/create-quote/quote-review/utils/coverage-value-formatters';

/**
 * Custom error class for unexpected API responses
 */
export class UnexpectedResponseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UnexpectedResponseError';
  }
}
@Injectable({
  providedIn: 'root',
})
export class LegacyPolicyResponseMapper {
  logger = inject(NgKLoggerService);
  primaryCoverageMapper = inject(PrimaryCoverageMapperService);
  floodProtectionMapper = inject(FloodProtectionMapperService);
  otherPolicyFeaturesMapper = inject(OtherPolicyFeaturesMapperService);
  rspsMapper = inject(RspsMapperService);

  // Replaces fromGetResponse and fromPatchResponse.
  mapPolicyResponse(response: LegacyApiPolicyResponse, throwOnMismatch = false): Partial<Policy> {
    const rspsCoverage = response.coverages?.main_coverages?.find((coverage) => coverage.component.attribute_name === 'roof_surface_payment_schedule');
    let mappedRsps: Endorsement[] = [];
    if (rspsCoverage) {
      const rspsResult = this.rspsMapper.map(rspsCoverage);
      mappedRsps = rspsResult ? [rspsResult] : [];
    }

    const limitedFungiCoverage = response.coverages.other_coverages?.find((coverage) => coverage.component.attribute_name === 'limited_fungi');

    const floodEndorsementCoverage = response.coverages?.other_coverages?.find((coverage) => coverage.component.attribute_name === 'flood_endorsement');
    const mappedFloodEndorsement = floodEndorsementCoverage ? this.floodProtectionMapper.mapFloodProtection([floodEndorsementCoverage]) : [];
    const otherCoveragesWithoutFlood = response.coverages?.other_coverages?.filter((coverage) => coverage.component.attribute_name !== 'flood_endorsement');

    const mappedFloodProtection = response.add_ons ? this.floodProtectionMapper.mapFloodProtection(response.add_ons) : [];

    const baseMapped: Partial<Policy> = {
      ...this.mapMetadata(response.metadata),
      ...this.mapRedirectTo(response.redirect_to),
      ...this.mapEffectiveDate(response.effective_date),
      ...this.mapDiscounts(response.automatically_applied_funnel_discounts),
      ...this.mapLimitedFungiMetadata(response.limited_fungi_metadata, limitedFungiCoverage),
      ...this.mapDeductibles(response.coverages.deductibles),
      ...this.mapAndValidateFees(response.fees),
      ...this.mapIsOwnerOccupied(response.is_owner_occupied),
    };

    // Always return partial only include coverages if present in response
    return {
      ...baseMapped,
      ...(response.coverages?.main_coverages && {
        primaryCoverages: this.primaryCoverageMapper.mapPrimaryCoverages(response.coverages.main_coverages, throwOnMismatch),
      }),
      ...(otherCoveragesWithoutFlood && {
        additionalCoverages: this.otherPolicyFeaturesMapper.mapOtherPolicyFeatures(otherCoveragesWithoutFlood),
      }),
      ...(response.add_ons && {
        endorsements: [...mappedFloodProtection, ...mappedFloodEndorsement, ...mappedRsps],
      }),
    };
  }

  private mapMetadata(metadata: LegacyApiPolicyMetadata | null): Partial<Policy> {
    if (!metadata) {
      this.logWarning('Metadata is missing from the response.');
      return {};
    }

    const primaryApplicant = metadata.applicants.find((applicant: LegacyApiApplicant) => !applicant.co_applicant);

    const parsedMetadata: Partial<Policy> = {
      address: metadata.address,
      firstName: primaryApplicant?.first_name,
      fullName: primaryApplicant?.name,
      policyType: metadata.policy_type?.label,
      premium: metadata.premium,
    };

    this.validateMetadata(parsedMetadata);
    return parsedMetadata;
  }

  private validateMetadata(parsedMetadata: Partial<Policy>): void {
    const requiredFields: (keyof Partial<Policy>)[] = ['address', 'firstName', 'fullName', 'policyType', 'premium'];
    requiredFields.forEach((field) => {
      if (!parsedMetadata[field]) {
        this.logWarning(`${field} is missing in metadata.`);
      }
    });
  }

  private mapRedirectTo(redirectTo: string | undefined): Partial<Policy> {
    if (!redirectTo) {
      this.logWarning('Redirect to is missing from the response.');
      return {};
    }
    return { redirectTo };
  }

  private mapEffectiveDate(effectiveDate: LegacyApiCoverage | undefined): Partial<Policy> {
    if (!effectiveDate?.value) {
      this.logWarning('Effective date is missing or invalid in the response.');
      return {};
    }

    // validate string is in YYYY-MM-DD format
    if (!isDateYYYYMMDD(effectiveDate.value.toString())) {
      this.logWarning('Effective date is not in YYYY-MM-DD format.');
      return {};
    }
    return { effectiveDate: effectiveDate.value.toString() };
  }

  private mapDiscounts(discounts: LegacyApiAutomaticallyAppliedFunnelDiscount | undefined): Partial<Policy> {
    if (!discounts) {
      this.logWarning('Discounts are missing from the response.');
      return {};
    }
    return {
      discounts: Object.entries(discounts).map(([label, value]) => ({ label, value: Number(value) })),
    };
  }

  private mapLimitedFungiMetadata(limitedFungiMetadata: LegacyApiLimitedFungiMetadata | undefined, limitedFungiCoverage: LegacyApiCoverage | undefined): Partial<Policy> {
    if (!limitedFungiMetadata) {
      if (limitedFungiCoverage) {
        this.logWarning('Expected Limited Fungi Metadata is missing from the response.');
      }
      return {};
    }

    return {
      limitedFungiMetadata: {
        firstCoverageLimit: limitedFungiMetadata.first_coverage_limit,
        secondCoverageLimit: limitedFungiMetadata.second_coverage_limit,
      },
    };
  }

  private mapDeductibles(deductibles: LegacyApiCoverage[] | undefined): Partial<Policy> {
    if (!deductibles) {
      this.logWarning('Deductibles are missing from the response.');
      return {};
    }

    // log an event if deductibles are ever more than 3
    if (deductibles && deductibles.length > 3) {
      this.logger.event({
        metaData: {
          context: 'LegacyPolicyResponseMapperService',
          message: 'More than 3 deductibles found in the response. This should never happen. The UI design assumes 3 or less. If we see this, escalate to design.',
          data: {
            deductibles,
          },
        },
        name: 'more-than-3-deductibles-found',
      });
    }

    const output = {
      deductibles: deductibles.map((deductible: LegacyApiCoverage) => ({
        id: deductible.component.attribute_name,
        value: this.validateDeductibleValue(deductible),
        options: this.mapAndValidateDeductibleOptions(deductible),
      })),
    };
    return output;
  }

  private validateDeductibleValue(deductible: LegacyApiCoverage): string | undefined {
    if (deductible.value === undefined || deductible.value === null) {
      this.logWarning(`Deductible value is missing or undefined for ${deductible.component.attribute_name}`);
      return undefined;
    }

    if (typeof deductible.value === 'boolean') {
      this.logWarning(`Deductible value is boolean for ${deductible.component.attribute_name}`);
      return undefined;
    }

    if (typeof deductible.value !== 'number' && typeof deductible.value !== 'string') {
      this.logWarning(`Deductible value is not a number or string for ${deductible.component.attribute_name}`);
      return undefined;
    }

    return deductible.value.toString();
  }

  private mapAndValidateDeductibleOptions(deductible: LegacyApiCoverage): { id: string; label: string; value: string }[] {
    if (!deductible.component.select_options) {
      return [];
    }

    return deductible.component.select_options.map((option, index) => {
      const { percentage, value } = parseCoverageValue(option.label);
      let formattedLabel = option.label;

      if (percentage !== null && value) {
        formattedLabel = `${value} (${percentage}% of Coverage A)`;
      } else if (percentage !== null) {
        formattedLabel = `${percentage}% of Coverage A`;
      } else if (value) {
        formattedLabel = value.toString();
      }

      return {
        id: `${deductible.component.attribute_name}-_option_${index + 1}`,
        label: formattedLabel,
        value: option.value.toString(),
      };
    });
  }

  private mapAndValidateFees(fees: LegacyApiFee[] | undefined): Partial<Policy> {
    if (!fees) {
      this.logWarning('Fees array is missing from the response');
      return { fees: [] };
    }

    return {
      fees: fees
        .filter((fee) => {
          if (!fee.id) {
            this.logWarning('Fee is missing required field: id');
            return false;
          }
          if (!fee.label) {
            this.logWarning('Fee is missing required field: label');
            return false;
          }
          if (!fee.formatted_value) {
            this.logWarning('Fee is missing required field: formatted_value');
            return false;
          }

          // Check for valid value
          if (fee.value === null || fee.value === undefined) {
            this.logWarning(`Fee value is missing or invalid for ${fee.id}`);
            return false;
          }
          if (typeof fee.value !== 'number') {
            this.logWarning(`Fee value must be a number for ${fee.id}`);
            return false;
          }
          if (fee.value < 0) {
            this.logWarning(`Fee value cannot be negative for ${fee.id}`);
            return false;
          }

          return true;
        })
        .map((fee) => ({
          id: fee.id,
          value: fee.value,
          label: fee.label,
          formattedValue: fee.formatted_value,
        })),
    };
  }

  private mapIsOwnerOccupied(isOwnerOccupied: boolean | undefined): Partial<Policy> {
    // If isOwnerOccupied is undefined, don't include it in the returned object
    // This preserves the optional nature of the property
    if (isOwnerOccupied === undefined) {
      return {};
    }

    return { isOwnerOccupied };
  }

  private logWarning(message: string): void {
    this.logger.error({
      priority: 'P3',
      context: `LegacyPolicyResponseMapperService`,
      message: `Unexpected response from Policies controller: "${message}"`,
    });
  }
}
